import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useParams } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useGetEngineerJob, getGetEngineerJobQueryKey, getListEngineerJobsQueryKey, getListAdminEngineerJobsQueryKey, useUpdateEngineerJobReport, JobReportInput } from "@workspace/api-client-react";
import { ChevronLeft, Save, CheckCircle, Loader2, AlertCircle, MapPin, Phone, Mail as MailIcon, User as UserIcon, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SignaturePad from "@/components/signature-pad";

export default function EngineerJobDetail() {
  const { data: user, isLoading: isAuthLoading } = useAuth();
  const [, setLocation] = useLocation();
  const params = useParams();
  const id = parseInt(params.id || "0", 10);
  
  const queryClient = useQueryClient();
  const { data: job, isLoading: isJobLoading, isFetching, refetch, isError } = useGetEngineerJob(id, {
    query: {
      enabled: !!user && id > 0,
      queryKey: getGetEngineerJobQueryKey(id),
      refetchOnMount: "always",
      refetchInterval: 30000,
    }
  });
  
  const updateReport = useUpdateEngineerJobReport();
  const { toast } = useToast();

  const initializedRef = useRef<number | null>(null);
  const [formData, setFormData] = useState<JobReportInput | null>(null);
  const [formVersion, setFormVersion] = useState<number | null>(null);
  const isReadOnly = user?.role !== "engineer" || job?.status === "submitted" || job?.status === "cancelled";
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [conflictError, setConflictError] = useState(false);

  useEffect(() => {
    if (!isAuthLoading && !user) setLocation("/admin");
  }, [user, isAuthLoading, setLocation]);

  useEffect(() => {
    if (job && !isFetching && initializedRef.current !== id) {
      setFormData({
        visitDate: job.report.visitDate || "",
        findings: job.report.findings || "",
        workCompleted: job.report.workCompleted || "",
        partsUsed: job.report.partsUsed || "",
        recommendations: job.report.recommendations || "",
        systemStatus: job.report.systemStatus || "",
        surveyRequirements: job.report.surveyRequirements || "",
        proposedSolution: job.report.proposedSolution || "",
        engineerSignature: job.report.engineerSignature || "",
        clientSignature: job.report.clientSignature || "",
      });
      
      setFormVersion(job.version);
      initializedRef.current = id;
    }
  }, [job, isFetching, id]);

  const isDirty = useMemo(() => {
    if (!job || !formData) return false;
    if (job.report.visitDate !== formData.visitDate) return true;
    if (job.report.findings !== formData.findings) return true;
    if (job.report.workCompleted !== formData.workCompleted) return true;
    if (job.report.partsUsed !== formData.partsUsed) return true;
    if (job.report.recommendations !== formData.recommendations) return true;
    if (job.report.systemStatus !== formData.systemStatus) return true;
    if (job.report.surveyRequirements !== formData.surveyRequirements) return true;
    if (job.report.proposedSolution !== formData.proposedSolution) return true;
    if (job.report.engineerSignature !== formData.engineerSignature) return true;
    if (job.report.clientSignature !== formData.clientSignature) return true;
    return false;
  }, [job, formData]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !isReadOnly) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, isReadOnly]);

  const handleBack = () => {
    if (isDirty && !isReadOnly) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to leave?")) {
        return;
      }
    }
    setLocation(backUrl);
  };

  if (isAuthLoading || isJobLoading || (!job && !isError)) {
    return <div className="min-h-[100dvh] bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  
  if (isError || !job) {
    return (
      <div className="min-h-[100dvh] bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Job Unavailable</h2>
        <p className="text-slate-600 mb-6">The job could not be loaded. It may have been reassigned or removed.</p>
        <button onClick={() => setLocation(user?.role === "admin" ? "/admin/engineer-jobs" : "/engineer")} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">Return to Dashboard</button>
      </div>
    );
  }

  if (!formData || initializedRef.current !== id) return <div className="p-8 text-center">Loading report…</div>;

  const updateField = (path: keyof JobReportInput, value: any) => {
    if (isReadOnly || updateReport.isPending) return;
    setFormData(prev => prev ? { ...prev, [path]: value } : prev);
  };

  const validateForComplete = (): string[] => {
    if (!formData) return ["Form data is missing"];
    const errors: string[] = [];

    if (!formData.visitDate) errors.push("Visit date is required");
    if (!formData.findings?.trim()) errors.push("Findings are required");
    if (!formData.recommendations?.trim()) errors.push("Recommendations are required");
    if (!formData.engineerSignature?.trim()) errors.push("Engineer signature is required");

    if (job.kind === "callout") {
      if (!formData.workCompleted?.trim()) errors.push("Work completed is required for callouts");
      if (!formData.systemStatus) errors.push("System status must be selected for callouts");
    } else if (job.kind === "site_survey") {
      if (!formData.surveyRequirements?.trim()) errors.push("Survey requirements are required");
      if (!formData.proposedSolution?.trim()) errors.push("Proposed solution is required");
    }

    return errors;
  };

  const handleSave = (submit: boolean) => {
    if (!formData || formVersion === null || isReadOnly || updateReport.isPending) return;
    
    if (submit) {
      const errs = validateForComplete();
      if (errs.length > 0) {
        setValidationErrors(errs);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }
    
    setValidationErrors([]);
    setConflictError(false);
    
    updateReport.mutate({
      id,
      data: {
        version: formVersion,
        submit,
        report: formData
      }
    }, {
      onSuccess: (updatedJob) => {
        queryClient.setQueryData(getGetEngineerJobQueryKey(id), updatedJob);
        setFormVersion(updatedJob.version);
        setFormData(updatedJob.report);
        void queryClient.invalidateQueries({ queryKey: getListEngineerJobsQueryKey() });
        void queryClient.invalidateQueries({ queryKey: getListAdminEngineerJobsQueryKey() });
        toast({ title: submit ? "Report submitted" : "Draft saved successfully" });
        if (submit && user?.role === "engineer") {
          setLocation(job.kind === "callout" ? "/engineer/callouts" : "/engineer/site-surveys");
        }
      },
      onError: (err: any) => {
        if (err.message?.includes("version") || err.message?.includes("conflict") || err.status === 409) {
          setConflictError(true);
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          toast({ title: "Save failed", description: err.message, variant: "destructive" });
        }
      }
    });
  };
  
  const handleReloadConflict = async () => {
    if (!window.confirm("Discard your unsaved changes and load the latest report?")) return;
    const result = await refetch();
    if (result.data && !result.isError) {
      setFormData(result.data.report);
      setFormVersion(result.data.version);
      setConflictError(false);
    }
  };

  const backUrl = user?.role === "admin" 
    ? "/admin/engineer-jobs" 
    : job.kind === "callout" ? "/engineer/callouts" : "/engineer/site-surveys";

  const isCallout = job.kind === "callout";

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col pb-24">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm print:hidden">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={handleBack} className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex flex-col">
              <span className="font-bold text-slate-900 leading-tight">
                {job.status === "submitted" ? "Submitted Report" : "Edit Draft"}
              </span>
              <span className="text-[10px] text-slate-500 font-medium">#{id} - {isCallout ? "Call Out" : "Site Survey"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(job.status === "submitted" || user?.role === "admin") && (
              <button onClick={() => window.print()} className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                Print
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto p-4 md:p-6 space-y-6 print:p-0 print:m-0 print:max-w-none">
        {/* Print Header */}
        <div className="hidden print:block text-center mb-8 border-b-2 border-slate-900 pb-4">
          <h1 className="text-2xl font-bold text-slate-900">{isCallout ? "CALL OUT REPORT" : "SITE SURVEY REPORT"}</h1>
          <p className="text-sm font-bold text-slate-600">Vertex Climate Engineering</p>
        </div>
        
        {conflictError && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 print:hidden">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 text-sm">Version Conflict</h3>
                <p className="text-sm text-amber-700 mt-1">
                  This job has been updated elsewhere since you opened it. Saving now would overwrite those changes.
                </p>
                <button 
                  onClick={handleReloadConflict}
                  className="mt-3 px-3 py-1.5 bg-amber-200 hover:bg-amber-300 text-amber-900 text-xs font-semibold rounded transition-colors"
                >
                  Reload Latest Version (Discards your current edits)
                </button>
              </div>
            </div>
          </div>
        )}

        {validationErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 print:hidden">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 text-sm">Cannot submit report</h3>
                <ul className="mt-2 space-y-1 text-sm text-red-700 list-disc list-inside">
                  {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}

        <Section title="JOB DETAILS">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col sm:flex-row print:border-none print:shadow-none mb-6">
            {job.site.coverImagePath && (
              <div className="w-full sm:w-1/3 h-40 sm:h-auto bg-slate-100 relative shrink-0 print:w-32 print:h-32">
                <img src={`${import.meta.env.BASE_URL}${job.site.coverImagePath.replace(/^\//, "")}`} alt="Site cover" className="w-full h-full object-cover absolute inset-0" />
              </div>
            )}
            <div className="p-4 flex-1 print:px-0">
              <h4 className="font-bold text-slate-900 text-lg flex items-center flex-wrap gap-2">
                {job.site.name || "Unnamed Site"}
                {job.site.brand ? <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded-full font-bold uppercase tracking-wider print:hidden">{job.site.brand}</span> : null}
              </h4>
              <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap print:text-xs">{job.site.address || "No address provided"}</p>
              
              <div className="mt-4 grid grid-cols-1 gap-2 print:mt-2">
                {job.site.contactName && (
                  <div className="flex items-center gap-2 text-sm text-slate-700 print:text-xs">
                    <UserIcon className="w-4 h-4 text-slate-400 shrink-0 print:w-3 print:h-3" />
                    <span>{job.site.contactName}</span>
                  </div>
                )}
                {job.site.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-700 print:text-xs">
                    <Phone className="w-4 h-4 text-slate-400 shrink-0 print:w-3 print:h-3" />
                    <a href={`tel:${job.site.phone}`} className="hover:text-primary transition-colors text-slate-700 print:text-slate-900">{job.site.phone}</a>
                  </div>
                )}
                {job.site.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-700 print:text-xs">
                    <MailIcon className="w-4 h-4 text-slate-400 shrink-0 print:w-3 print:h-3" />
                    <a href={`mailto:${job.site.email}`} className="hover:text-primary transition-colors text-slate-700 print:text-slate-900">{job.site.email}</a>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="space-y-4 pt-4 border-t border-slate-100">
             <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Task Title</p>
                <p className="text-sm font-medium text-slate-900">{job.title}</p>
             </div>
             <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Description / Instructions</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{job.description || "No description provided."}</p>
             </div>
             
             <div className="flex items-center gap-2 pt-2">
               <Calendar className="w-4 h-4 text-slate-400" />
               <span className="text-sm font-medium text-slate-700">Scheduled: {new Date(job.scheduledDate).toLocaleDateString()}</span>
             </div>
          </div>
        </Section>
        
        <fieldset disabled={isReadOnly || updateReport.isPending}>
        <Section title="REPORT DETAILS">
          <div className="space-y-5">
            <Field label="Visit Date" type="date" value={formData.visitDate || ""} onChange={v => updateField("visitDate", v)} disabled={isReadOnly} />
            
            {isCallout && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Findings (Optional during draft)</label>
                  <textarea
                    value={formData.findings || ""}
                    onChange={e => updateField("findings", e.target.value)}
                    disabled={isReadOnly}
                    placeholder="Enter diagnostic findings..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Work Completed (Optional during draft)</label>
                  <textarea
                    value={formData.workCompleted || ""}
                    onChange={e => updateField("workCompleted", e.target.value)}
                    disabled={isReadOnly}
                    placeholder="Detail work performed..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Parts Used (Optional)</label>
                  <textarea
                    value={formData.partsUsed || ""}
                    onChange={e => updateField("partsUsed", e.target.value)}
                    disabled={isReadOnly}
                    placeholder="List parts used..."
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 resize-y"
                  />
                </div>
                
                <div className="pt-2">
                  <p className="text-sm font-medium text-slate-900 mb-2">System Status</p>
                  <div className="flex flex-col gap-2">
                    <Radio label="Operational" name="systemStatus" value="operational" current={formData.systemStatus || ""} onChange={v => updateField("systemStatus", v)} disabled={isReadOnly} />
                    <Radio label="Follow Up Required" name="systemStatus" value="follow_up_required" current={formData.systemStatus || ""} onChange={v => updateField("systemStatus", v)} disabled={isReadOnly} />
                    <Radio label="Isolated / Off" name="systemStatus" value="isolated" current={formData.systemStatus || ""} onChange={v => updateField("systemStatus", v)} disabled={isReadOnly} />
                  </div>
                </div>
              </>
            )}
            
            {!isCallout && (
               <>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Survey Requirements (Optional during draft)</label>
                  <textarea
                    value={formData.surveyRequirements || ""}
                    onChange={e => updateField("surveyRequirements", e.target.value)}
                    disabled={isReadOnly}
                    placeholder="Client requirements and objectives..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Findings (Optional during draft)</label>
                  <textarea
                    value={formData.findings || ""}
                    onChange={e => updateField("findings", e.target.value)}
                    disabled={isReadOnly}
                    placeholder="Site observations and measurements..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Proposed Solution (Optional during draft)</label>
                  <textarea
                    value={formData.proposedSolution || ""}
                    onChange={e => updateField("proposedSolution", e.target.value)}
                    disabled={isReadOnly}
                    placeholder="Recommended system design or equipment..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 resize-y"
                  />
                </div>
               </>
            )}
            
            <div className="h-px bg-slate-100" />
            
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Recommendations / Further Actions</label>
              <textarea
                value={formData.recommendations || ""}
                onChange={e => updateField("recommendations", e.target.value)}
                disabled={isReadOnly}
                placeholder="Recommendations..."
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 resize-y"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <SignaturePad label="Engineer signature" value={formData.engineerSignature || ""} onChange={v => updateField("engineerSignature", v)} disabled={isReadOnly} />
              <SignaturePad label="Client signature (Optional)" value={formData.clientSignature || ""} onChange={v => updateField("clientSignature", v)} disabled={isReadOnly} />
            </div>
            
            {job.status === "submitted" && (
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-center gap-2 text-emerald-800">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold text-sm">Submitted by {job.submittedByName} on {job.submittedAt ? new Date(job.submittedAt).toLocaleDateString() : ""}</span>
              </div>
            )}
          </div>
        </Section>
        </fieldset>

      </main>

      {!isReadOnly && !conflictError && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-40 print:hidden">
          <div className="max-w-3xl mx-auto flex items-center gap-3 justify-end">
            <button
              onClick={() => handleSave(false)}
              disabled={updateReport.isPending}
              className="px-6 py-2.5 rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> {updateReport.isPending ? "Saving..." : "Save Draft"}
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={updateReport.isPending}
              className="px-6 py-2.5 rounded-lg bg-primary text-white hover:bg-primary/90 text-sm font-medium transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" /> {updateReport.isPending ? "Submitting..." : "Submit Report"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string, children: React.ReactNode }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm print:border-none print:shadow-none print:break-inside-avoid">
      <div className="px-5 py-3 bg-slate-900 border-b border-slate-800 print:bg-white print:border-slate-300 print:px-0 print:py-2">
        <h2 className="text-white font-bold text-sm tracking-wide print:text-slate-900 print:text-lg">{title}</h2>
      </div>
      <div className="p-5 print:px-0">
        {children}
      </div>
    </section>
  );
}

function Field({ label, value, onChange, disabled, type = "text" }: { label: string, value: string, onChange: (v: string) => void, disabled?: boolean, type?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 disabled:text-slate-700 print:border-none print:border-b print:rounded-none print:px-0 print:bg-transparent"
      />
    </div>
  );
}

function Radio({ label, name, value, current, onChange, disabled }: { label: string, name: string, value: string, current: string, onChange: (v: string) => void, disabled?: boolean }) {
  const isChecked = current === value;
  return (
    <label className={`flex items-center gap-2 cursor-pointer group ${disabled ? 'opacity-80' : ''}`}>
      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isChecked ? 'border-primary bg-primary' : 'border-slate-300 bg-white group-hover:border-primary/50'}`}>
        {isChecked && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
      </div>
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input type="radio" name={name} value={value} checked={isChecked} onChange={() => onChange(value)} disabled={disabled} className="sr-only" />
    </label>
  );
}
